import { Link } from "react-router-dom";
import { TrendingUp, Target, Trophy, Gamepad2 } from "lucide-react";
import { TierBadge } from "@/components/TierBadge";

const positionAccent = (idx) => {
  if (idx === 0) return { color: "#E2B13C", glow: "rgba(226, 177, 60, 0.5)" };
  if (idx === 1) return { color: "#A0A5A9", glow: "rgba(160, 165, 169, 0.4)" };
  if (idx === 2) return { color: "#A8765A", glow: "rgba(168, 118, 90, 0.4)" };
  return { color: "#6E6A5E", glow: "transparent" };
};

export function PlayerRow({ player, index }) {
  const pos = positionAccent(index);
  const displayName = player.nickname || player.game_name;

  return (
    <Link
      to={`/player/${player.id}`}
      data-testid={`leaderboard-row-${index}`}
      className="block group border-b border-[#2A2A30]/60 hover:bg-[#1A1A1D] transition-colors"
    >
      {/* MOBILE LAYOUT (< md) */}
      <div className="md:hidden px-3 py-3">
        <div className="flex items-center gap-3">
          <PositionBadge index={index} pos={pos} small />
          {player.profile_icon_url ? (
            <img
              src={player.profile_icon_url}
              alt={displayName}
              className="h-10 w-10 rounded-md border border-[#2A2A30] object-cover shrink-0"
              loading="lazy"
            />
          ) : (
            <div className="h-10 w-10 rounded-md border border-[#2A2A30] bg-[#1A1A1D] shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="font-bold text-[#F0E6D2] truncate group-hover:text-[#C8AA6E] transition-colors font-[Outfit] text-sm">
                {displayName}
              </span>
              <span className="text-[9px] uppercase tracking-wider text-[#6E6A5E] font-bold px-1 py-0.5 rounded border border-[#2A2A30] shrink-0">
                {player.region}
              </span>
            </div>
            <div className="text-[11px] text-[#6E6A5E] truncate">
              {player.game_name}#{player.tag_line}
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <TierBadge
            tier={player.tier}
            rank={player.rank}
            lp={player.league_points}
          />
          <div className="flex items-center gap-2.5 text-[11px] tabular-nums ml-auto">
            <MobileStat icon={Gamepad2} value={player.plays} />
            <MobileStat
              icon={Target}
              value={player.avg_placement ? player.avg_placement.toFixed(2) : "—"}
              highlight={player.avg_placement && player.avg_placement <= 4.0}
            />
            <MobileStat
              icon={Trophy}
              value={player.plays ? `${Math.round(player.top4_rate)}%` : "—"}
            />
            <MobileStat
              icon={TrendingUp}
              value={player.plays ? `${Math.round(player.win_rate)}%` : "—"}
              highlight={player.win_rate >= 20}
            />
          </div>
        </div>
      </div>

      {/* DESKTOP LAYOUT (>= md) */}
      <div className="hidden md:grid grid-cols-12 items-center gap-3 px-5 py-4">
        <div className="col-span-1 flex items-center">
          <PositionBadge index={index} pos={pos} />
        </div>

        <div className="col-span-4 flex items-center gap-3 min-w-0">
          {player.profile_icon_url ? (
            <img
              src={player.profile_icon_url}
              alt={displayName}
              className="h-11 w-11 rounded-md border border-[#2A2A30] object-cover"
              loading="lazy"
            />
          ) : (
            <div className="h-11 w-11 rounded-md border border-[#2A2A30] bg-[#1A1A1D]" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-bold text-[#F0E6D2] truncate group-hover:text-[#C8AA6E] transition-colors font-[Outfit]">
                {displayName}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-[#6E6A5E] font-bold px-1.5 py-0.5 rounded border border-[#2A2A30]">
                {player.region}
              </span>
            </div>
            <div className="text-xs text-[#6E6A5E] truncate">
              {player.game_name}#{player.tag_line}
            </div>
          </div>
        </div>

        <div className="col-span-3 flex justify-start">
          <TierBadge tier={player.tier} rank={player.rank} lp={player.league_points} />
        </div>

        <div className="col-span-4 grid grid-cols-4 gap-2 text-right">
          <Stat icon={Gamepad2} value={player.plays} label="Gier" />
          <Stat
            icon={Target}
            value={player.avg_placement ? player.avg_placement.toFixed(2) : "—"}
            label="Śr. miej."
            highlight={player.avg_placement && player.avg_placement <= 4.0}
          />
          <Stat
            icon={Trophy}
            value={player.plays ? `${Math.round(player.top4_rate)}%` : "—"}
            label="Top 4"
          />
          <Stat
            icon={TrendingUp}
            value={player.plays ? `${Math.round(player.win_rate)}%` : "—"}
            label="Wygrane"
            highlight={player.win_rate >= 20}
          />
        </div>
      </div>
    </Link>
  );
}

function PositionBadge({ index, pos, small }) {
  const size = small ? "h-8 w-8 text-base" : "h-9 w-9 text-lg";
  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-md border font-black font-[Outfit] ${size}`}
      style={{
        color: pos.color,
        borderColor: `${pos.color}55`,
        backgroundColor: `${pos.color}10`,
        boxShadow: index < 3 ? `0 0 18px ${pos.glow}` : "none",
      }}
    >
      {index + 1}
    </div>
  );
}

function Stat({ icon: Icon, value, label, highlight }) {
  return (
    <div className="text-right">
      <div
        className={`flex items-center justify-end gap-1 font-bold font-[Outfit] ${
          highlight ? "text-[#0AC8B9]" : "text-[#F0E6D2]"
        }`}
      >
        <Icon className="h-3.5 w-3.5 text-[#6E6A5E]" />
        <span className="tabular-nums">{value}</span>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-[#6E6A5E]">{label}</div>
    </div>
  );
}

function MobileStat({ icon: Icon, value, highlight }) {
  return (
    <div
      className={`flex items-center gap-1 font-bold font-[Outfit] ${
        highlight ? "text-[#0AC8B9]" : "text-[#F0E6D2]"
      }`}
    >
      <Icon className="h-3 w-3 text-[#6E6A5E] shrink-0" />
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
